import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Users, Loader2, Briefcase, Check } from 'lucide-react';
import { conveniosApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConveniosManagement() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);  // for owner selector
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({
    name: '', days_per_year: 5, description: '',
    vacation_days_per_year: '', vacation_day_type: 'habiles',
    telework_enabled: true, owner_user_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState({});
  const [currentMembers, setCurrentMembers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await conveniosApi.list();
      setItems(res.data);
    } catch {
      toast.error('Error al cargar convenios');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    // Owner selector — only admin can change ownership, so only admin needs the users list
    if (user?.role === 'admin') {
      axios.get(`${API}/users`).then(r => setUsers(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const openCreate = () => {
    setForm({ name: '', days_per_year: 5, description: '', vacation_days_per_year: '', telework_enabled: true, owner_user_id: '' });
    setDialog({ mode: 'create' });
  };
  const openEdit = (item) => {
    setForm({
      name: item.name,
      days_per_year: item.days_per_year,
      description: item.description || '',
      vacation_days_per_year: item.vacation_days_per_year ?? '',
      telework_enabled: item.telework_enabled !== false,
      owner_user_id: item.owner_user_id || '',
    });
    setDialog({ mode: 'edit', item });
  };
  const openDelete = (item) => setDialog({ mode: 'delete', item });

  const openAssign = async (item) => {
    setDialog({ mode: 'assign', item });
    setSelectedUsers({});
    setUserSearch('');
    try {
      const [eligRes, membersRes] = await Promise.all([
        axios.get(`${API}/convenios/${item.id}/eligible-users`),
        conveniosApi.listUsers(item.id),
      ]);
      setUsersList(eligRes.data);
      setCurrentMembers(membersRes.data);
      // Pre-select current members so admin/owner sees who's already in
      const sel = {};
      membersRes.data.forEach(u => { sel[u.id] = true; });
      setSelectedUsers(sel);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando usuarios');
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    if (form.days_per_year < 0 || form.days_per_year > 60) { toast.error('Días entre 0 y 60'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        days_per_year: parseInt(form.days_per_year, 10),
        description: form.description || '',
        vacation_days_per_year: form.vacation_days_per_year === '' ? null : parseInt(form.vacation_days_per_year, 10),
        vacation_day_type: form.vacation_day_type || 'habiles',
        telework_enabled: !!form.telework_enabled,
        owner_user_id: form.owner_user_id || null,
      };
      if (dialog.mode === 'create') {
        await conveniosApi.create(payload);
        toast.success('Convenio creado');
      } else {
        await conveniosApi.update(dialog.item.id, payload);
        toast.success('Convenio actualizado');
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const runBulkAssign = async () => {
    const ids = Object.keys(selectedUsers).filter(k => selectedUsers[k]);
    if (!dialog?.item) return;
    setSaving(true);
    try {
      // Compute diff vs current members
      const currentIds = new Set(currentMembers.map(m => m.id));
      const toAssign = ids.filter(id => !currentIds.has(id));
      const toRemove = currentMembers.filter(m => !selectedUsers[m.id]).map(m => m.id);

      if (toAssign.length === 0 && toRemove.length === 0) {
        toast.info('No hay cambios');
        setDialog(null);
        return;
      }
      if (toAssign.length) {
        await conveniosApi.assignUsers(dialog.item.id, toAssign, dialog.item.id);
      }
      if (toRemove.length) {
        await axios.post(`${API}/convenios/${dialog.item.id}/assign`, { user_ids: toRemove, convenio_id: null });
      }
      toast.success(`Actualizado: +${toAssign.length} / -${toRemove.length}`);
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await conveniosApi.remove(dialog.item.id);
      toast.success('Convenio eliminado');
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const runAssign = async () => {
    const ids = Object.keys(selectedUsers).filter(k => selectedUsers[k]);
    if (ids.length === 0) { toast.error('Selecciona al menos un usuario'); return; }
    setSaving(true);
    try {
      await conveniosApi.assignUsers(dialog.item.id, ids, dialog.item.id);
      toast.success(`${ids.length} usuario(s) asignados a ${dialog.item.name}`);
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const filteredUsers = userSearch
    ? usersList.filter(u =>
        `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase()))
    : usersList.slice(0, 50);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-primary" />
            Convenios y Asuntos Propios
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Define los convenios colectivos y el máximo anual de días de asuntos propios. Asigna usuarios a cada convenio.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/convenios/sync-balances`);
                  const d = res.data;
                  toast.success(`Sincronizado: ${d.updated}/${d.checked} usuarios actualizados (año ${d.year})`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error');
                }
              }}
              data-testid="btn-sync-balances"
            >
              Sincronizar saldos
            </Button>
          )}
          <Button onClick={openCreate} data-testid="btn-new-convenio" disabled={user?.role !== 'admin' && user?.role !== 'manager'}>
            <Plus className="h-4 w-4 mr-2" /> Nuevo convenio
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          Aún no hay convenios. Crea el primero para empezar.
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map(c => (
            <Card key={c.id} data-testid={`convenio-card-${c.id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="truncate">{c.name}</span>
                  <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">
                    {c.days_per_year} AP/año
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                <div className="flex flex-wrap gap-1.5">
                  {c.vacation_days_per_year != null && (
                    <span className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded">
                      {c.vacation_days_per_year} vac/año ({c.vacation_day_type === 'naturales' ? 'naturales' : 'hábiles'})
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded ${c.telework_enabled !== false
                    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
                    : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'}`}>
                    {c.telework_enabled !== false ? 'Teletrabajo OK' : 'Sin teletrabajo'}
                  </span>
                  {c.owner_user_id && (
                    <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 rounded">
                      Responsable asignado
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  {c.user_count} usuario{c.user_count !== 1 ? 's' : ''} asignados
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => openAssign(c)} data-testid={`btn-assign-${c.id}`}>
                    <Users className="h-3.5 w-3.5 mr-1" /> Asignar
                  </Button>
                  {(user?.role === 'admin' || c.owner_user_id === user?.id) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`btn-edit-${c.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => openDelete(c)} data-testid={`btn-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialog?.mode === 'create' || dialog?.mode === 'edit'} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'create' ? 'Nuevo convenio' : 'Editar convenio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Convenio Oficinas"
                data-testid="convenio-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Días de asuntos propios al año *</Label>
              <Input
                type="number" min={0} max={60}
                value={form.days_per_year}
                onChange={e => setForm({ ...form, days_per_year: parseInt(e.target.value || '0', 10) })}
                data-testid="convenio-days"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Días de vacaciones al año (opcional)</Label>
              <Input
                type="number" min={0} max={60}
                placeholder="Vacío = usar default global (24)"
                value={form.vacation_days_per_year}
                onChange={e => setForm({ ...form, vacation_days_per_year: e.target.value })}
                data-testid="convenio-vacation-days"
              />
              <p className="text-xs text-muted-foreground">Si se rellena, sobrescribe los 24 días generales para los usuarios de este convenio.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de día *</Label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio" name="day_type" value="habiles"
                    checked={form.vacation_day_type === 'habiles'}
                    onChange={e => setForm({ ...form, vacation_day_type: e.target.value })}
                    className="h-4 w-4 accent-primary"
                    data-testid="convenio-day-type-habiles"
                  />
                  <span><strong>Días hábiles</strong> (excluye fines de semana y festivos)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio" name="day_type" value="naturales"
                    checked={form.vacation_day_type === 'naturales'}
                    onChange={e => setForm({ ...form, vacation_day_type: e.target.value })}
                    className="h-4 w-4 accent-primary"
                    data-testid="convenio-day-type-naturales"
                  />
                  <span><strong>Días naturales</strong> (cuenta TODOS los días)</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground">Define cómo se calculan los días de vacaciones consumidos al solicitar un rango.</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <div>
                <Label className="cursor-pointer" htmlFor="conv-tw">Permite teletrabajo</Label>
                <p className="text-xs text-muted-foreground">Si está desactivado, los usuarios del convenio no podrán solicitar teletrabajo.</p>
              </div>
              <input
                id="conv-tw" type="checkbox"
                checked={form.telework_enabled}
                onChange={e => setForm({ ...form, telework_enabled: e.target.checked })}
                className="h-4 w-4 accent-primary"
                data-testid="convenio-telework"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Responsable del convenio (opcional)</Label>
              {user?.role === 'admin' ? (
                <select
                  value={form.owner_user_id || ''}
                  onChange={e => setForm({ ...form, owner_user_id: e.target.value })}
                  data-testid="convenio-owner"
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="">— Sólo admin —</option>
                  {users.filter(u => u.role === 'manager' || u.role === 'admin').map(u => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                  ))}
                </select>
              ) : (
                <p className="text-xs text-muted-foreground italic bg-muted/40 rounded-md px-3 py-2">
                  Serás el responsable automáticamente. Sólo podrás asignar usuarios de tu jerarquía.
                </p>
              )}
              {user?.role === 'admin' && (
                <p className="text-xs text-muted-foreground">Este usuario podrá asignar/desasignar personas de su jerarquía a este convenio.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Descripción (opcional)</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="convenio-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={dialog?.mode === 'delete'} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Eliminar convenio</DialogTitle></DialogHeader>
          <p className="text-sm">¿Eliminar <strong>{dialog?.item?.name}</strong>? Los usuarios asignados quedarán sin convenio.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={remove} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign users dialog */}
      <Dialog open={dialog?.mode === 'assign'} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Asignar usuarios → {dialog?.item?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Selecciona los usuarios que pertenecerán a este convenio. Ya hay <strong>{currentMembers.length}</strong> usuario(s) asignados.
            </p>
            <Input
              placeholder="Buscar por nombre o email…"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              data-testid="convenio-user-search"
            />
            <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
              {filteredUsers.map(u => {
                const isMember = currentMembers.some(m => m.id === u.id);
                return (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!selectedUsers[u.id]}
                      onChange={() => setSelectedUsers(s => ({ ...s, [u.id]: !s[u.id] }))}
                      className="h-4 w-4 accent-primary"
                      data-testid={`convenio-user-${u.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {u.first_name} {u.last_name}
                        {isMember && <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">ya asignado</span>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                  </label>
                );
              })}
              {filteredUsers.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">No hay usuarios</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
            <Button onClick={runAssign} disabled={saving} data-testid="convenio-bulk-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Asignar ({Object.values(selectedUsers).filter(Boolean).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
